import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Tags, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { SubcoBadge, TagChip } from './SubcoBits';

const selCls = 'w-full h-8 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-xs';

export function TagPicker({ tags, value, onChange, onTagsChanged, canAdd = true }) {
  const [name, setName] = useState('');
  const toggle = (t) => onChange(value.includes(t) ? value.filter((x) => x !== t) : [...value, t]);
  const add = async () => {
    if (!name.trim()) return;
    try {
      const r = await api.post('/user-tags', { name: name.trim() });
      onTagsChanged?.(r.data.data);
      onChange([...value, r.data.data.find((t) => t.toLowerCase() === name.trim().toLowerCase())].filter(Boolean));
      setName('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Etiket eklenemedi'); }
  };
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1">
        {tags.map((t) => <TagChip key={t} tag={t} active={value.includes(t)} onClick={() => toggle(t)} testId={`tag-chip-${t}`} />)}
      </div>
      {canAdd && (
        <div className="flex gap-1">
          <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
            placeholder="Yeni etiket" className={selCls} data-testid="tag-new-input" />
          <button type="button" onClick={add} className="h-8 px-2 rounded-md bg-slate-900 text-white" data-testid="tag-new-add"><Plus className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
  );
}

export function SubcoTagCell({ u, subcos, tags, onTagsChanged, onUpdated, canEdit }) {
  const [open, setOpen] = useState(false);
  const [subcoId, setSubcoId] = useState(u.subco_id || '');
  const [firmId, setFirmId] = useState(u.firm_id || '');
  const [sel, setSel] = useState(u.tags || []);
  const [busy, setBusy] = useState(false);
  const subco = subcos.find((s) => s.id === (u.subco_id || ''));
  const picked = subcos.find((s) => s.id === subcoId);
  const firmOpts = picked?.firms?.length ? picked.firms : null;
  const reset = (o) => { setOpen(o); if (o) { setSubcoId(u.subco_id || ''); setFirmId(u.firm_id || ''); setSel(u.tags || []); } };
  const save = async () => {
    if (firmOpts && !firmOpts.some((f) => f.id === firmId)) return toast.error('Subco\'nun çalıştığı firmalardan birini seçin');
    setBusy(true);
    try {
      const patch = {};
      if (subcoId !== (u.subco_id || '') || firmId !== (u.firm_id || '')) {
        const r = await api.put(`/subco-companies/users/${u.id}`, { subco_id: subcoId || null, firm_id: firmId || null });
        Object.assign(patch, r.data.data);
        const f = picked?.firms?.find((x) => x.id === r.data.data.firm_id);
        if (f) patch.firm_name = f.name;
      }
      if (JSON.stringify(sel) !== JSON.stringify(u.tags || [])) {
        patch.tags = (await api.put(`/user-tags/users/${u.id}`, { tags: sel })).data.data;
      }
      onUpdated({ ...u, ...patch });
      toast.success(`${u.full_name} güncellendi`);
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Güncellenemedi'); }
    finally { setBusy(false); }
  };
  const chips = (
    <span className="flex flex-wrap items-center gap-1">
      <SubcoBadge subco={subco} name={u.subco_name} testId={`user-subco-badge-${u.id}`} />
      {(u.tags || []).map((t) => <TagChip key={t} tag={t} active testId={`user-tag-${u.id}-${t}`} />)}
    </span>
  );
  if (!canEdit || u.role === 'SUBCO' || u.role === 'SUPER_ADMIN') return chips;
  return (
    <Popover open={open} onOpenChange={reset}>
      <PopoverTrigger asChild>
        <button type="button" className="mt-0.5 flex flex-wrap items-center gap-1 text-left group" data-testid={`user-subco-tags-btn-${u.id}`}>
          {chips}
          <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-400 group-hover:text-slate-700"><Tags className="h-3 w-3" />{!u.subco_name && !(u.tags || []).length ? 'Subco / etiket' : ''}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-3" align="start" data-testid={`user-subco-tags-pop-${u.id}`}>
        <div className="space-y-1">
          <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Subco</div>
          <select value={subcoId} onChange={(e) => { setSubcoId(e.target.value); const s = subcos.find((x) => x.id === e.target.value); if (s?.firm_ids?.length && !s.firm_ids.includes(firmId)) setFirmId(s.firm_ids[0]); }} className={selCls} data-testid={`user-subco-select-${u.id}`}>
            <option value="">— Subco yok (doğrudan firma) —</option>
            {subcos.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {firmOpts && (
            <select value={firmId} onChange={(e) => setFirmId(e.target.value)} className={selCls} data-testid={`user-subco-firm-${u.id}`}>
              {firmOpts.map((f) => <option key={f.id} value={f.id}>Firma: {f.name}</option>)}
            </select>
          )}
        </div>
        <div className="space-y-1">
          <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Etiketler</div>
          <TagPicker tags={tags} value={sel} onChange={setSel} onTagsChanged={onTagsChanged} />
        </div>
        <button type="button" onClick={save} disabled={busy} className="w-full h-8 rounded-md bg-slate-900 text-white text-xs font-semibold flex items-center justify-center gap-1 disabled:opacity-50" data-testid={`user-subco-save-${u.id}`}>
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Kaydet
        </button>
      </PopoverContent>
    </Popover>
  );
}
